import { useCallback, useEffect, useRef, useState } from 'react';
import ProgressContainer from '@/components/app-main/progress-container/progress-container';
import SalesOrderLineInterface from '@/interfaces/SalesOrderLineInterface';
import SalesOrderLine from '@/classes/SalesOrderLine';
import Loader from '@/components/loader/loader';
import OrdersServices from '@/services/orders-services';
import { ShowErrorResponseMessage, ShowResponseMessage } from '@/utility/api-utility';
import { AxiosError } from 'axios';
import SalesOrder from '@/classes/SalesOrder';
import StackIcon from './../../assets/icons/stack-icon.svg?react';
import ArrowRight from './../../assets/icons/arrow-right.svg?react';
import OrdersList from './orders-list/orders-list';
import QuantityModal from '../modals/quantity-modal/quantity-modal';
import ReviewConfirmationModal from '../modals/review-confirmation-modal/review-confirmation-modal';
import ProductPreviewModal from '../modals/product-preview-modal/product-preview-modal';
import ReviewModal from '../modals/review-modal/review-modal';
import ChangePersonalisationModal from '../modals/change-personalisation-modal/change-personalisation-modal';
import RemovePersonalisationModal from '../modals/remove-personalisation-modal/remove-personalisation-modal';
import PersonalisationModal from '../modals/personalisation-modal/personalisation-modal';
import LinesUtility from '@/utility/lines-utility';
import { getUrlParameters } from '@/utility/utility';
import RecipeInterface from '@/interfaces/Recipe';
import { getStorage, setStorage } from '@/utility/storage';
import { useStrings } from '@/contexts/strings-context';
import { toast } from 'sonner';

interface AppMainProps {
  awaitingOrders?: SalesOrderLineInterface[];
  completedOrders?: SalesOrderLineInterface[];
  onEdit?: (order: SalesOrderLineInterface) => void;
  onPersonaliseSeveral?: () => void;
  onUseExistingDesign?: (order: SalesOrderLineInterface) => void;
}

export const AppMain = ({ onPersonaliseSeveral }: AppMainProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [orderLoaded, setOrderLoaded] = useState(false);
  const [order, setOrder] = useState<SalesOrder>({} as SalesOrder);
  const [line, setLine] = useState<SalesOrderLineInterface | null>(null);
  const [imagePreview, setImagePreview] = useState('');
  const [productsChecked, setProductsChecked] = useState(false);
  const [lineToChange, setLineToChange] = useState<SalesOrderLineInterface | null>(null);
  const [lineToRemove, setLineToRemove] = useState<SalesOrderLineInterface | null>(null);
  const [lineToPersonalise, setLineToPersonalise] = useState<SalesOrderLineInterface | null>(null);
  const [showQuantityModal, setShowQuantityModal] = useState(false);
  const [showReviewConfirmationModal, setShowReviewConfirmationModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showChangeModal, setShowChangeModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showPersonalisationModal, setShowPersonalisationModal] = useState(false);
  const [quantityToPersonalise, setQuantityToPersonalise] = useState(0);
  const [previousRecipes, setPreviousRecipes] = useState<RecipeInterface[]>([]);
  const previousRecipesRef = useRef<RecipeInterface[]>([]);
  const recipeRequestsRef = useRef(new Set<string>());
  const strings = useStrings();

  const addPreviousRecipes = useCallback((recipes: RecipeInterface[]) => {
    previousRecipesRef.current = [
      ...new Map(
        [...previousRecipesRef.current, ...recipes].map((recipe) => [
          JSON.stringify([recipe.product_item_no_2, recipe.recipe_id]),
          recipe,
        ]),
      ).values(),
    ];
    setPreviousRecipes(previousRecipesRef.current);
  }, []);

  const preapplyRecipes = useCallback(
    async (recipes: RecipeInterface[], lines: SalesOrderLineInterface[], orderNo: string) => {
      addPreviousRecipes(recipes);
      const productIDs = [...new Set(lines.map((line) => line.Item_No_2.split(' ').slice(0, 2).join('+')))];
      const sourceRecipes = [
        ...new Map(recipes.filter((recipe) => recipe.recipe_id).map((recipe) => [recipe.recipe_id, recipe])).values(),
      ];

      await Promise.all(
        sourceRecipes.flatMap(({ recipe_id: recipeID }) =>
          productIDs.map(async (productID) => {
            const requestKey = JSON.stringify([orderNo, productID, recipeID]);
            const hasRecipe = previousRecipesRef.current.some(
              (recipe) => recipe.product_item_no_2 === productID && recipe.recipe_id === recipeID,
            );
            if (hasRecipe || recipeRequestsRef.current.has(requestKey)) return;
            recipeRequestsRef.current.add(requestKey);

            try {
              const { data: response } = await OrdersServices.applyRecipe({ orderNo, recipeID, productID });
              const { data: newRecipe } = response;
              if (!newRecipe?.RecipeID) throw new Error('The recipe response did not include a recipe ID.');
              addPreviousRecipes([
                {
                  product_item_no_2: productID,
                  recipe_id: newRecipe.RecipeID,
                  image: newRecipe.image,
                },
              ]);
              // Keep successful source/target pairs cached even when the API returns a new recipe ID.
            } catch (error) {
              recipeRequestsRef.current.delete(requestKey);
              console.error(`Failed to apply recipe for ${productID}`, error);
            }
          }),
        ),
      );
    },
    [addPreviousRecipes],
  );

  useEffect(() => {
    const { order: orderNo = '' } = getUrlParameters();

    const fetchOrders = async () => {
      try {
        if (isLoading) return;
        setIsLoading(true);

        const { data: response } = await OrdersServices.fetch({ orderNo });
        const { data } = response;
        const { Reference = '' } = data;

        data.SalesLines?.Sales_Order_Line.forEach((l: SalesOrderLineInterface) => (l.Expanded = true));
        setOrder(new SalesOrder(data));
        const lines = data.SalesLines?.Sales_Order_Line ?? [];
        const recipes = LinesUtility.recipes(lines);
        setProductsChecked(recipes.length > 0);
        setStorage('reference', Reference);
        void preapplyRecipes(recipes, lines, data.No ?? '');
      } catch (error) {
        ShowErrorResponseMessage({ error: error as AxiosError });
      } finally {
        setIsLoading(false);
        setOrderLoaded(true);
      }
    };

    fetchOrders();
  }, [preapplyRecipes]);

  const onPersonalise = (line: SalesOrderLineInterface) => {
    setLine(line);
    setShowQuantityModal(true);
  };

  const handlePersonaliseClose = () => setShowQuantityModal(false);

  const handlePersonaliseContinue = (
    line: SalesOrderLineInterface,
    quantity: number,
    previousDesign: RecipeInterface | null,
  ) => {
    // USE EXISTING
    if (previousDesign) {
      applyPersonalisation(previousDesign, line, quantity);
    }
    // PERSONALISE
    else {
      setLineToPersonalise(line);
      setQuantityToPersonalise(quantity);
      setShowPersonalisationModal(true);
    }

    setShowQuantityModal(false);
  };
  const isEveryLinePersonalised = order?.SalesLines?.Sales_Order_Line.every((item) => item.Line_Notes !== '');

  const handleConfirmationButtonClick = () => setShowReviewModal(true);

  const handleConfirmReviewConfirmationModal = async () => {
    const { order: orderNo = '' } = getUrlParameters();
    const email = getStorage('email');

    try {
      if (isLoading) return;
      setIsLoading(true);
      setShowReviewConfirmationModal(false);
      setShowReviewModal(false);

      await OrdersServices.moderate({ orderNo, email });

      setOrder((previousOrder) => ({ ...previousOrder, Open_Hold: true }));
      ShowResponseMessage({
        response: {
          type: 'success',
          message: 'Your personalised products are ready and your order has been confirmed.',
          title: 'Personalisation complete',
        },
      });
    } catch (error) {
      ShowErrorResponseMessage({ error: error as AxiosError });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReviewConfirmationModalClose = () => {
    setShowReviewConfirmationModal(false);
    setShowReviewModal(true);
  };

  const updateLine = (line: SalesOrderLineInterface) => {
    const lines = order?.SalesLines?.Sales_Order_Line.map((item) => ({ ...item }));

    lines.forEach((orderLine, index) => {
      if (orderLine.Line_No === line.Line_No && orderLine.Item_No_2 === line.Item_No_2) {
        orderLine = new SalesOrderLine(line);
        lines[index] = orderLine;
      }
    });

    setOrder({ ...order, SalesLines: { Sales_Order_Line: [...lines.map((l) => new SalesOrderLine(l))] } });
  };

  const handlePreviewClick = (imageUrl: string) => {
    setImagePreview(imageUrl);
    setShowPreviewModal(true);
  };

  const handlePreviewModalClose = () => {
    setImagePreview('');
    setShowPreviewModal(false);
  };

  const handleChangeClick = (line: SalesOrderLineInterface) => {
    setLineToChange(line);
    setShowChangeModal(true);
  };

  const handleChangeConfirm = (line: SalesOrderLineInterface) => {
    if (!line) return;

    setLine({ ...line, Line_To_Change: true });
    setShowQuantityModal(true);
    setShowChangeModal(false);
  };

  const handleChangeClose = () => {
    setLineToChange(null);
    setShowChangeModal(false);
  };

  const handleRemoveClick = (line: SalesOrderLineInterface) => {
    setLineToRemove(line);
    setShowRemoveModal(true);
  };

  const handleReviewClose = () => {
    setShowReviewModal(false);
  };

  const handleReviewConfirm = () => {
    setShowReviewConfirmationModal(true);
  };

  const handleRemoveConfirm = async (line: SalesOrderLineInterface) => {
    setIsLoading(true);
    try {
      const { No: orderNo = '' } = order;
      const { Line_No: lineNo = '', External_Document_Line_No: extNo = '' } = line;
      const lines = order?.SalesLines?.Sales_Order_Line ?? [];

      const { data: response } = await OrdersServices.removeLine({ orderNo, lineNo: '' + lineNo, extNo: '' + extNo });
      const { data: { lines: linesToUpdate } = [] } = response;
      linesToUpdate.forEach((line: SalesOrderLineInterface) => (line.Expanded = true));

      // remove all product lines
      const filteredLines = [...lines].filter((l) => l.External_Document_Line_No !== line.External_Document_Line_No);
      const updatedLines = [...filteredLines, ...linesToUpdate].sort((a, b) => +a.Line_No - +b.Line_No);

      setOrder({ ...order, SalesLines: { Sales_Order_Line: [...updatedLines] } });
    } catch (error) {
      console.error('Failed to remove personalisation:', error);
      ShowErrorResponseMessage({ error: error as AxiosError });
    } finally {
      setIsLoading(false);
      setLineToRemove(null);
      setShowRemoveModal(false);
    }
  };

  const handleRemoveClose = () => {
    setLineToRemove(null);
    setShowRemoveModal(false);
  };

  const handlePersonalisationClose = () => {
    setLineToPersonalise(null);
    setQuantityToPersonalise(0);
    setShowPersonalisationModal(false);
  };

  const handlePersonalisationConfirm = (recipe: RecipeInterface) => {
    if (lineToPersonalise) applyPersonalisation(recipe, lineToPersonalise, quantityToPersonalise);
  };

  const applyPersonalisation = async (recipe: RecipeInterface, line: SalesOrderLineInterface, quantity: number) => {
    setIsLoading(true);

    try {
      const { No: orderNo = '' } = order;
      // Item_No_2
      const { Line_No: lineNo = '', Item_No_2 = '' } = line;
      const lines = order?.SalesLines?.Sales_Order_Line ?? [];
      const { recipe_id = '', image = '' } = recipe;
      const remainingPersonalisationQuantity = LinesUtility.remainingPersonalisationQuantity(lines, line);
      const shouldSplitLine = line.Line_To_Change !== true && quantity < remainingPersonalisationQuantity;
      const updatedSelectedLine = { Line_No: line.Line_No, Line_Notes: recipe_id, Product_URL: image };

      if (shouldSplitLine) await OrdersServices.splitLine({ orderNo, lineNo: '' + lineNo, qty: '' + quantity });

      const { data: response } = await OrdersServices.updateOrder({ orderNo, lines: [updatedSelectedLine] });
      const { data: linesToUpdate = [] } = response;
      linesToUpdate.forEach((line: SalesOrderLineInterface) => (line.Expanded = true));

      // remove all lines from same products and re-add them from API
      const filteredLines = [...lines].filter((l) => l.External_Document_Line_No !== line.External_Document_Line_No);
      const updatedLines = [...filteredLines, ...linesToUpdate].sort((a, b) => +a.Line_No - +b.Line_No);

      updatedLines.forEach((l: SalesOrderLineInterface) => {
        if (l.External_Document_Line_No === line.External_Document_Line_No) l.Expanded = true;
      });

      setOrder({ ...order, SalesLines: { Sales_Order_Line: [...updatedLines] } });

      addPreviousRecipes(LinesUtility.recipes(updatedLines));
      void preapplyRecipes(
        [{ ...recipe, product_item_no_2: String(Item_No_2).split(' ').slice(0, 2).join('+') }],
        updatedLines,
        orderNo,
      );
    } catch (error) {
      console.error('Failed to update personalisation:', error);
      ShowErrorResponseMessage({ error: error as AxiosError });
    } finally {
      setIsLoading(false);
      handlePersonalisationClose();
    }
  };

  const scrollToTop = (): Promise<void> => {
    return new Promise((resolve) => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });

      const checkScroll = () => {
        if (window.scrollY <= 1) {
          window.removeEventListener('scroll', checkScroll);
          resolve();
        }
      };

      window.addEventListener('scroll', checkScroll);
      checkScroll();
    });
  };

  const handleProductsCheckedButtonClick = async () => {
    setProductsChecked(true);
    await scrollToTop();
    toast.success('Success', { description: 'You can now personalise your products!', duration: 5000 });
  };

  const { Reference = '' } = order;

  return (
    <main className="min-h-0 w-full flex-1 bg-slate-50 pt-[8vh]">
      <div className="mx-auto w-full max-w-[1120px] px-[16px] sm:py-[24px] sm:px-[30px] lg:py-[38px]">
        {orderLoaded && (
          <>
            <div
              className={`grid grid-cols-1 gap-[22px] ${productsChecked ? 'sm:grid-cols-[minmax(0,1fr)_390px]' : ''} sm:items-start sm:gap-[40px]`}
            >
              <div className="flex flex-col items-start justify-center lg:justify-start max-w-[500px] h-full">
                {productsChecked && (
                  <>
                    <h1 className="text-[26px] font-bold leading-[31px] tracking-[-0.02em] sm:text-[32px] sm:leading-[37px] mt-10 md:mt-0">
                      {order.Open_Hold ? 'Your personalised products' : strings.MAIN_TITLE}
                    </h1>
                    <h6 className="font-[Arial] text-slate-500 mt-5 font-bold">{Reference}</h6>
                  </>
                )}

                {productsChecked === false && (
                  <>
                    <h1 className="text-[26px] font-bold leading-[31px] tracking-[-0.02em] sm:text-[32px] sm:leading-[37px] mt-10 md:mt-0">
                      {strings.CHECK_TITLE}
                    </h1>
                    <h6 className="font-[Arial] text-slate-500 mt-5 font-bold">{Reference}</h6>
                  </>
                )}

                {productsChecked && (
                  <p className="mt-[12px] max-w-[full] text-[14px] font-medium leading-[20px] text-slate-600 sm:text-[16px] font-[Arial]">
                    {order.Open_Hold
                      ? 'Your personalisation is complete. You can review your designs below.'
                      : strings.MAIN_CONTENT}
                  </p>
                )}
              </div>

              {productsChecked && <ProgressContainer lines={order?.SalesLines?.Sales_Order_Line} />}

              {productsChecked === false && (
                <span
                  className="-mt-[18px] text-[14px] font-medium leading-[20px] text-slate-600 sm:text-[16px] font-[Arial]"
                  dangerouslySetInnerHTML={{ __html: strings.CHECK_CONTENT }}
                />
              )}
            </div>

            <section className="mt-[34px] mb-[34px] grid-cols-1 gap-[18px] border border-[#d1d1d1] bg-white  px-[18px] py-[18px] md:grid-cols-[minmax(0,1fr)_auto] md:items-center md:gap-[24px] md:px-[24px] hidden">
              <div className="flex min-w-0 items-start gap-[14px] md:items-center">
                <span aria-hidden="true" className="flex h-[44px] w-[44px] shrink-0 items-center justify-center">
                  <StackIcon className="fill-[#0053a0]" />
                </span>

                <div className="min-w-0">
                  <h2 className="text-[clamp(16px,calc(14px_+_0.3vw),17px)] font-bold leading-[20px]">
                    Want the same design on several products?
                  </h2>
                  <p className="mt-[3px] text-[16px] leading-[17px] text-slate-500">
                    Select the products and create the design once. We'll apply it to all selected pieces that still
                    need personalisation.
                  </p>
                </div>
              </div>

              <button
                className="button-screwfix-success inline-flex min-h-[44px] w-full shrink-0 items-center justify-center px-[18px] text-center font-bold md:w-auto"
                onClick={onPersonaliseSeveral}
                type="button"
              >
                Use one design on several
              </button>
            </section>

            <OrdersList
              lines={order?.SalesLines?.Sales_Order_Line}
              onChangeLine={handleChangeClick}
              onRemoveLine={handleRemoveClick}
              onPersonalise={onPersonalise}
              onPreviewClick={handlePreviewClick}
              updateLine={updateLine}
              readOnly={order.Open_Hold}
              productsChecked={productsChecked}
            />

            {order.Open_Hold && <div className="mb-10"></div>}

            {order.Open_Hold === false && (
              <aside className="mt-[34px] flex flex-col items-start lg:items-center gap-[16px] border border-[#d1d1d1] bg-white px-[16px] py-[16px] lg:flex-row lg:px-[24px] mb-20">
                <div className="flex items-center gap-[14px]">
                  <div>
                    {productsChecked && (
                      <h2 className="text-[clamp(20px,calc(16px_+_0.3vw),24px)] font-bold leading-[20px]">
                        {strings.CONFIRM_TITLE}
                      </h2>
                    )}

                    <p className="mt-[4px] text-[13px] sm:text-[16px] leading-[17px] font-medium text-slate-500 font-[Arial]">
                      {productsChecked ? strings.CONFIRM_CONTENT : strings.CHECK_CONFIRMATION}
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-center lg:ml-auto lg:max-w-[400px] flex-1 w-full">
                  {productsChecked && (
                    <button
                      className="inline-flex w-full min-h-[42px] flex-1 items-center justify-center text-center gap-[12px] rounded-[6px] bg-blue-600 px-[18px] text-[16px] font-bold button-screwfix-success has-icon"
                      disabled={isEveryLinePersonalised === false}
                      onClick={handleConfirmationButtonClick}
                      type="button"
                    >
                      <span className="flex flex-1 items-center">{strings.CONFIRM_BUTTON}</span>
                      <ArrowRight className="h-[1em] w-[1em] fill-white " />
                    </button>
                  )}

                  {productsChecked === false && (
                    <button
                      className="inline-flex w-full min-h-[42px] flex-1 items-center justify-center text-center gap-[12px] rounded-[6px] bg-blue-600 px-[18px] text-[16px] font-bold button-screwfix-success has-icon"
                      onClick={handleProductsCheckedButtonClick}
                      // disabled={getStorage('input') !== null}
                      type="button"
                    >
                      <span className="flex flex-1 items-center">{strings.CHECK_BUTTON}</span>
                      <ArrowRight className="h-[1em] w-[1em] fill-white " />
                    </button>
                  )}
                </div>
              </aside>
            )}
          </>
        )}
      </div>
      <Loader visible={isLoading} />

      {line && (
        <QuantityModal
          open={showQuantityModal}
          line={line}
          lines={order?.SalesLines?.Sales_Order_Line}
          previousRecipes={LinesUtility.previousRecipes(line, previousRecipes)}
          onClose={handlePersonaliseClose}
          onContinue={handlePersonaliseContinue}
        />
      )}

      {lineToPersonalise && (
        <PersonalisationModal
          open={showPersonalisationModal}
          onConfirm={handlePersonalisationConfirm}
          onGoBack={handlePersonalisationClose}
          line={lineToPersonalise}
        />
      )}

      <ProductPreviewModal open={showPreviewModal} imageUrl={imagePreview} onClose={handlePreviewModalClose} />

      {lineToChange && (
        <ChangePersonalisationModal
          open={showChangeModal}
          line={lineToChange}
          onConfirm={handleChangeConfirm}
          onGoBack={handleChangeClose}
        />
      )}

      {lineToRemove && (
        <RemovePersonalisationModal
          open={showRemoveModal}
          line={lineToRemove}
          onConfirm={handleRemoveConfirm}
          onGoBack={handleRemoveClose}
          isSubmitting={isLoading}
        />
      )}

      <ReviewModal
        open={showReviewModal}
        lines={LinesUtility.groupByProduct(order?.SalesLines?.Sales_Order_Line)}
        onConfirm={handleReviewConfirm}
        onGoBack={handleReviewClose}
      />

      <ReviewConfirmationModal
        open={showReviewConfirmationModal}
        onConfirm={handleConfirmReviewConfirmationModal}
        onGoBack={handleReviewConfirmationModalClose}
      />
    </main>
  );
};

export default AppMain;
